<template>
  <v-sheet class="list-view-container">
    <!-- Table Section -->
    <v-data-table
      class="list-view-table elevation-0 d-flex flex-column table-sticky custom-table flex-grow-1"
      ref="table"
      :headers="visibleHeaders"
      :items="entries"
      :loading="loading"
      :options.sync="tableOptions"
      :server-items-length="total"
      :footer-props="{
        showFirstLastPage: true,
        firstIcon: 'mdi-chevron-double-left',
        lastIcon: 'mdi-chevron-double-right',
        prevIcon: 'mdi-chevron-left',
        nextIcon: 'mdi-chevron-right',
        itemsPerPageText: 'Row/Page',
        itemsPerPageOptions: [15, 30, 50, 100, 200, 300, 400, 500],
      }">
      
      <!-- Add the columns control to the header -->
      <template v-slot:top>
        <div class="d-flex justify-start pa-2">
          <columns-visibility-control
            v-if="taskType"
            v-model="availableColumns"
            :defaults="defaultColumnsWithCustomFields"
            :withColumnsOrder="true"
            :storageKey="storageKey"
          />
        </div>
      </template>

      <!-- Name Column -->
      <template v-slot:[`item.text`]="{ item }">
        <div class="d-flex align-center" @click="update(item)">
          <!-- Color circle -->
          <v-avatar v-if="item.color" size="16" class="mr-2" :color="item.color">
            <v-icon small :color="item.color">mdi-circle</v-icon>
          </v-avatar>

          <span class="task-name font-weight-medium">{{ item.text }}</span>
        </div>
      </template>

      <!-- Description Column -->
      <template v-slot:[`item.taskDescription`]="{ item }">
        <span class="text-body-2 description-text">{{ item.taskDescription || '—' }}</span>
      </template>

      <!-- Status Column -->
      <template v-slot:[`item.status.name`]="{ item }">
        <div v-if="isEditing(item.id, 'status')" class="d-flex align-center my-2">
          <v-select
            :items="taskType.statuses"
            item-value="id"
            item-text="name"
            v-model="editingValue"
            dense
            outlined
            hide-details
            auto-select-first
            @keydown.enter="saveEdit(item)"
            @keydown.escape="cancelEdit"
            ref="statusEditor">
          </v-select>
          <v-btn icon small @click="saveEdit(item)" :loading="isSaving(item.id, 'status')">
            <v-icon small color="success">mdi-check</v-icon>
          </v-btn>
          <v-btn icon small @click="cancelEdit">
            <v-icon small color="error">mdi-close</v-icon>
          </v-btn>
        </div>
        <v-chip
          v-else-if="item.status"
          small
          :color="getStatusColor(item.status.color)"
          text-color="white"
          class="status-chip"
          @click="startEdit(item, 'status', item.statusId)">
          {{ item.status.name }}
        </v-chip>
      </template>

      <!-- Participants Column -->
      <template v-slot:[`item.participants`]="{ item }">
        <div v-if="isEditing(item.id, 'participants')" class="d-flex align-center my-2">
          <user-selector
            v-model="editingValue"
            label="Participants"
            multiple
            dense
            hide-details
            style="min-width: 200px;"
            @blur="cancelEdit"
            @keydown.escape="cancelEdit"
            ref="participantsEditor">
          </user-selector>
          <v-btn icon small @click="saveEdit(item)" :loading="isSaving(item.id, 'participants')">
            <v-icon small color="success">mdi-check</v-icon>
          </v-btn>
          <v-btn icon small @click="cancelEdit">
            <v-icon small color="error">mdi-close</v-icon>
          </v-btn>
        </div>
        <div v-else class="d-flex align-center" @click="startEdit(item, 'participants', item.participantsIds)">
          <!-- Single participant -->
          <template v-if="item.participants && item.participants.length === 1">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <user-avatar
                  v-bind="attrs"
                  v-on="on"
                  :user="item.participants[0]"
                  :size="32"
                  disable-open-link
                  hide-roles
                  show-no-user>
                </user-avatar>
              </template>
              <div class="user-tooltip">
                <div class="d-flex align-center">
                  <v-avatar size="24" class="mr-2" :color="getAvatarColor(item.participants[0])">
                    <template v-if="item.participants[0].avatarUrl">
                      <v-img :src="item.participants[0].avatarUrl"></v-img>
                    </template>
                    <template v-else>
                      {{ getUserInitials(item.participants[0]) }}
                    </template>
                  </v-avatar>
                  <span class="font-weight-medium">{{ item.participants[0].firstName }} {{ item.participants[0].lastName }}</span>
                </div>
              </div>
            </v-tooltip>
          </template>
          
          <!-- Multiple participants -->
          <template v-else-if="item.participants && item.participants.length > 1">
            <div class="participants-stack">
              <v-tooltip
                v-for="(user, index) in item.participants.slice(0, 3)"
                :key="index"
                bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-avatar
                    v-bind="attrs"
                    v-on="on"
                    :size="28"
                    :style="{ zIndex: item.participants.length - index, marginLeft: index > 0 ? '-12px' : '0' }"
                    :color="getAvatarColor(user)"
                    class="participant-avatar">
                    <template v-if="user.avatarUrl">
                      <v-img :src="user.avatarUrl"></v-img>
                    </template>
                    <template v-else>
                      {{ getUserInitials(user) }}
                    </template>
                  </v-avatar>
                </template>
                <div class="user-tooltip">
                  <div class="d-flex align-center">
                    <v-avatar size="24" class="mr-2" :color="getAvatarColor(user)">
                      <template v-if="user.avatarUrl">
                        <v-img :src="user.avatarUrl"></v-img>
                      </template>
                      <template v-else>
                        {{ getUserInitials(user) }}
                      </template>
                    </v-avatar>
                    <span class="font-weight-medium">{{ user.firstName }} {{ user.lastName }}</span>
                  </div>
                </div>
              </v-tooltip>
              <v-tooltip bottom v-if="item.participants.length > 3">
                <template v-slot:activator="{ on, attrs }">
                  <v-chip
                    v-bind="attrs"
                    v-on="on"
                    x-small
                    color="primary"
                    class="ml-1">
                    +{{ item.participants.length - 3 }}
                  </v-chip>
                </template>
                <div>
                  <div v-for="(user, index) in item.participants.slice(3)" :key="index" class="d-flex align-center mb-1">
                    <v-avatar size="20" class="mr-2" :color="getAvatarColor(user)">
                      <template v-if="user.avatarUrl">
                        <v-img :src="user.avatarUrl"></v-img>
                      </template>
                      <template v-else>
                        {{ getUserInitials(user) }}
                      </template>
                    </v-avatar>
                    <span>{{ user.firstName }} {{ user.lastName }}</span>
                  </div>
                </div>
              </v-tooltip>
            </div>
          </template>
          
          <!-- No participants -->
          <span v-else class="text-caption grey--text">
						<v-icon small color="grey" class="mr-1">mdi-cancel</v-icon>
						No Participants
					</span>
        </div>
      </template>

      <!-- Current Status Column -->
      <template v-slot:[`item.currentTaskStatus.name`]="{ item }">
        <v-chip
          v-if="item.currentTaskStatus"
          small
          :style="{ backgroundColor: item.currentTaskStatus.color }"
          text-color="white"
          class="status-chip">
          {{ item.currentTaskStatus.name }}
        </v-chip>
      </template>

      <!-- Progress Column -->
      <template v-slot:[`item.progress`]="{ item }">
        <div class="d-flex align-center">
          <v-progress-linear
            :value="item.progress"
            height="8"
            rounded
            :color="getProgressColor(item.progress)"
            class="mr-2">
          </v-progress-linear>
          <span class="text-caption">{{ item.progress }}%</span>
        </div>
      </template>

      <!-- Duration Column -->
      <template v-slot:[`item.duration`]="{ item }">
        <div class="d-flex align-center">
          <v-icon small color="grey" class="mr-1">mdi-clock-outline</v-icon>
          {{ item.duration }}h
        </div>
      </template>

      <!-- User Fields Columns (Reported By, Approved By, etc.) -->
      <template v-for="field in userFields" v-slot:[`item.${field}`]="{ item }">
        <div :key="field">
          <div v-if="isEditing(item.id, field)" class="d-flex align-center my-2">
            <user-selector
              v-model="editingValue"
              :label="getFieldLabel(field)"
              dense
              hide-details
              style="min-width: 200px;"
              @blur="cancelEdit"
              @keydown.escape="cancelEdit"
              :ref="`${field}Editor`">
            </user-selector>
            <v-btn icon small @click="saveEdit(item)" :loading="isSaving(item.id, field)">
              <v-icon small color="success">mdi-check</v-icon>
            </v-btn>
            <v-btn icon small @click="cancelEdit">
              <v-icon small color="error">mdi-close</v-icon>
            </v-btn>
          </div>
          <div v-else @click="startEdit(item, field, getFieldValue(item, field))">
            <user-avatar
              v-if="item[field]"
              :user="item[field]"
              disable-open-link
              hide-roles
              show-no-user>
            </user-avatar>
            <span v-else class="text-caption grey--text">
              <v-icon small color="grey" class="mr-1">mdi-account-plus</v-icon>
              No {{ getFieldLabel(field) }}
            </span>
          </div>
        </div>
      </template>

      <!-- Custom Fields Display -->
      <template v-for="field in customFields" v-slot:[`item.customFields.${field.id}`]="{ item }">
        <div class="d-flex align-center" :key="field.id">
          <!-- Dropdown fields -->
          <v-chip
            v-if="field.fieldType == TaskFieldType.Dropdown && getCustomFieldValue(item, field.id)"
            x-small
						label
            class="mr-1"
          >
            {{ getCustomFieldValue(item, field.id) }}
          </v-chip>
          
          <!-- Plan3D fields -->
          <v-chip
            v-else-if="field.fieldType == TaskFieldType.Plan3D && getCustomFieldOptionValue(field.id, getCustomFieldValue(item, field.id))"
            x-small
            class="mr-1"
						label
            :color="getCustomFieldOptionValue(field.id, getCustomFieldValue(item, field.id)).color || 'primary'"
            text-color="white"
          >
            {{ getCustomFieldOptionValue(field.id, getCustomFieldValue(item, field.id)).value }}
          </v-chip>
          
          <!-- User fields -->
          <template v-else-if="field.fieldType == TaskFieldType.Users && getCustomFieldValue(item, field.id)">
            <user-avatar
              v-if="getUserById(getCustomFieldValue(item, field.id))"
              :user="getUserById(getCustomFieldValue(item, field.id))"
              :size="32"
              disable-open-link
              hide-roles
              show-no-user
            ></user-avatar>
            <span v-else class="text-caption grey--text">
              <v-icon small color="grey" class="mr-1">mdi-account-question</v-icon>
              Unknown User
            </span>
          </template>
          
          <!-- Date fields -->
          <div 
            v-else-if="field.fieldType == TaskFieldType.Date && getCustomFieldValue(item, field.id)"
            class="date-cell"
          >
            <v-icon small color="grey" class="mr-1">mdi-calendar</v-icon>
            {{ $formatDate(getCustomFieldValue(item, field.id)) }}
          </div>
          
          <!-- Boolean fields -->
          <div v-else-if="field.fieldType == TaskFieldType.Boolean">
            <v-icon v-if="getCustomFieldValue(item, field.id) == 1" color="success">
              mdi-check-circle
            </v-icon>
            <v-icon v-else-if="getCustomFieldValue(item, field.id) == 0" color="error">
              mdi-close-circle
            </v-icon>
          </div>
          
          <!-- Number and Text fields (default) -->
          <span v-else-if="getCustomFieldValue(item, field.id)">
            {{ getCustomFieldValue(item, field.id) }}
          </span>
          
          <!-- Empty value -->
          <span v-else class="text-caption grey--text">—</span>
        </div>
      </template>

      <!-- Date Columns -->
      <template v-slot:[`item.start_date`]="{ item }">
        <div v-if="isEditing(item.id, 'start_date')" class="d-flex align-center my-2">
          <date-picker
            v-model="editingValue"
            label="Start Date"
            dense
            hide-details
            style="width: 120px;"
            @blur="cancelEdit"
            @keydown.escape="cancelEdit"
            ref="startDateEditor">
          </date-picker>
          <v-btn icon small @click="saveEdit(item)" :loading="isSaving(item.id, 'start_date')">
            <v-icon small color="success">mdi-check</v-icon>
          </v-btn>
          <v-btn icon small @click="cancelEdit">
            <v-icon small color="error">mdi-close</v-icon>
          </v-btn>
        </div>
        <div v-else class="date-cell" @click="startEdit(item, 'start_date', getFieldValue(item, 'start_date'))">
          <v-icon small color="grey" class="mr-1">mdi-calendar-start</v-icon>
          {{ $formatDate(item.start_date) }}
        </div>
      </template>

      <template v-slot:[`item.end_date`]="{ item }">
        <div v-if="isEditing(item.id, 'end_date')" class="d-flex align-center my-2">
          <date-picker
            v-model="editingValue"
            label="End Date"
            dense
            hide-details
            style="width: 120px;"
            @blur="cancelEdit"
            @keydown.escape="cancelEdit"
            ref="endDateEditor">
          </date-picker>
          <v-btn icon small @click="saveEdit(item)" :loading="isSaving(item.id, 'end_date')">
            <v-icon small color="success">mdi-check</v-icon>
          </v-btn>
          <v-btn icon small @click="cancelEdit">
            <v-icon small color="error">mdi-close</v-icon>
          </v-btn>
        </div>
        <div v-else class="date-cell" @click="startEdit(item, 'end_date', getFieldValue(item, 'end_date'))">
          <v-icon small color="grey" class="mr-1">mdi-calendar-end</v-icon>
          {{ $formatDate(item.end_date) }}
        </div>
      </template>

      <template v-slot:[`item.createDate`]="{ item }">
        <div class="date-cell">
          <v-icon small color="grey" class="mr-1">mdi-calendar</v-icon>
          {{ $formatDate(item.createDate) }}
        </div>
      </template>

      <!-- Actions Column -->
      <template v-slot:[`item.actions`]="{ item }">
        <div class="d-flex align-center">
          <v-btn icon small class="mr-1" v-has="permissions.Tasks.Update" @click.stop="update(item)">
            <v-icon small color="primary">mdi-pencil</v-icon>
          </v-btn>
          <v-btn icon small v-has="permissions.Tasks.Delete" @click.stop="del(item.id)">
            <v-icon small color="error">mdi-delete</v-icon>
          </v-btn>
          <TaskCopyLinkButton :task="item" />
          <TaskIsolateButton :task="item" />
        </div>
      </template>

      <!-- Loading State -->
      <template v-slot:loading>
        <div class="loading-container">
          <v-progress-circular indeterminate color="primary" size="32" width="4" class="mb-4">
          </v-progress-circular>
          <p class="text-subtitle-1 font-weight-medium">Loading Tasks...</p>
        </div>
      </template>

      <!-- No Data State -->
      <template v-slot:no-data>
        <div class="no-data-container">
          <v-icon large color="grey lighten-1" class="mb-4"> mdi-clipboard-text-outline </v-icon>
          <p class="text-subtitle-1 font-weight-medium">No Tasks Available</p>
        </div>
      </template>
    </v-data-table>

    <EditTask ref="editTask" @onEdit="onTaskUpdated"></EditTask>
  </v-sheet>
</template>

<script>
  // @workflow tasks.show-in-3d
  import { mapGetters } from 'vuex';
  import EditTask from '@Task/components/EditTask.vue';
  import TaskIsolateButton from '@Task/components/TaskIsolateButton.vue';
  import TaskCopyLinkButton from '@Task/components/TaskCopyLinkButton.vue';
  import UserAvatar from '@Share/components/UserAvatar.vue';
  import ColumnsVisibilityControl from '@Share/components/ColumnsVisibilityControl.vue';
  import UserSelector from '@Share/components/UserSelector.vue';
  import DatePicker from '@Share/components/VDatePicker.vue';
	import { TaskFieldType } from '@Share/config/enums';
  import { $formatDate } from '@Utils';
  import { $has } from '@/utils';
  import permissions from '@Share/config/permissions';
  import dayjs from 'dayjs';
  import ganttService from '@Task/service/gantt.service';
  import { log as debugLog, warn as debugWarn, error as debugError } from '@/utils/debugLogger';
  import { prefetchTasksIsolation } from '@Task/utils/taskIsolation';


  export default {
    name: 'list-view',
    components: {
      EditTask,
      TaskIsolateButton,
      TaskCopyLinkButton,
      UserAvatar,
      ColumnsVisibilityControl,
      UserSelector,
      DatePicker,
    },
    data() {
      return {
        availableColumns: [],
        permissions,
        userFields: [ 'approvedBy', 'reportedBy'],
        timerId: null,
        columnConfigTab: 0,
		TaskFieldType,
        editingCell: null, // { itemId: 'id', field: 'field_name' }
        editingValue: null,
        savingCell: null,
        loading: false,
        entries: [],
        total: 0,
        tableOptions: {
          page: 1,
          itemsPerPage: 50,
          sortBy: [],
          sortDesc: [],
        },
        isInitialLoad: true,
      };
    },
    computed: {
      ...mapGetters('USER', {
        users: 'GET_ENTRIES',
      }),
			storageKey() {
				return `listViewColumns-${this.$route.params.taskTypeId}`;
			},
      options: {
        get() {
          return this.$store.getters['TASK/GET_OPTIONS'];
        },
        set(val) {
          this.$store.commit('TASK/SET_OPTIONS', val);
        },
      },
      visibleHeaders() {
        return this.availableColumns
          .filter((col) => !col.hidden)
          .map((col) => ({
            text: col.text,
            value: col.value,
            width: col.width,
            sortable: col.sortable !== false,
            align: col.align || 'left',
          }));
      },
      defaultColumns() {
        return [
          { text: 'Actions', value: 'actions', hidden: false, width: 100, sortable: false, order: 1, hidable: false },

          // Standard Fields
          { text: 'Task Name', value: 'text', hidden: false, width: 250, order: 2, hidable: true },
          { text: 'Description', value: 'taskDescription', hidden: false, width: 300, order: 2.5, hidable: true },
          { text: 'Status', value: 'status.name', hidden: false, width: 250, order: 3, hidable: true },
          { text: 'Scheduled', value: 'currentTaskStatus.name', hidden: false, width: 150, order: 4, hidable: true },
          { text: 'Progress', value: 'progress', hidden: false, width: 120, order: 5, hidable: true },
          { text: 'Duration', value: 'duration', hidden: false, width: 100, order: 6, hidable: true },
          { text: 'Type', value: 'type.name', hidden: true, width: 150, order: 8, hidable: true },
          
          // Date Fields
          { text: 'Start Date', value: 'start_date', hidden: false, width: 150, order: 12, hidable: true },
          { text: 'End Date', value: 'end_date', hidden: false, width: 150, order: 13, hidable: true },
          { text: 'Actual Start Date', value: 'actualStartDate', hidden: true, width: 150, order: 14, hidable: true },
          { text: 'Actual End Date', value: 'actualEndDate', hidden: true, width: 150, order: 15, hidable: true },
          { text: 'Create Date', value: 'createDate', hidden: true, width: 150, order: 16, hidable: true },
          { text: 'Update Date', value: 'updateDate', hidden: true, width: 150, order: 17, hidable: true },
          
          // User Fields
          { text: 'Participants', value: 'participants', hidden: false, width: 150, order: 18, hidable: true },
          { text: 'Reported By', value: 'reportedBy', hidden: false, width: 150, order: 19, hidable: true },
          { text: 'Approved By', value: 'approvedBy', hidden: false, width: 150, order: 20, hidable: true },
          { text: 'Created By', value: 'createdBy', hidden: true, width: 150, order: 22, hidable: true },
          { text: 'Updated By', value: 'updatedBy', hidden: true, width: 150, order: 23, hidable: true },
        ];
      },
      defaultColumnsWithCustomFields() {
        // Return the full set of default columns plus custom field columns
        let combinedColumns = [...this.defaultColumns];
        if (this.customFieldsColumns && this.customFieldsColumns.length) {
          let lastOrder = Math.max(...combinedColumns.map(col => col.order || 0), 0);
          this.customFieldsColumns.forEach(field => {
            lastOrder++;
            if (!combinedColumns.find(col => col.value === field.value)) {
              combinedColumns.push({
                ...field,
                order: lastOrder,
              });
            }
          });
        }
        return combinedColumns;
      },
      getCustomFields() {
        // Get all custom fields from entries
        if (!this.entries || this.entries.length === 0) return [];
        
        const allCustomFields = [];
        
        // Collect all unique custom fields from tasks
        this.entries.forEach(task => {
          if (task.customFields && task.customFields.length > 0) {
            task.customFields.forEach(field => {
              const exists = allCustomFields.some(f => f.fieldId === field.fieldId);
              if (!exists) {
                allCustomFields.push(field);
              }
            });
          }
        });
        
        return allCustomFields;
      },
			taskType() {
				return this.$store.getters['TASK_MANAGER/GET_CURRENT_TASK_TYPE'];
			},
			customFields() {
				if (!this.taskType) return [];
				return this.taskType.sections.flatMap(section => section.fields);
			},
			customFieldsColumns() {
				return this.customFields.map(field => ({
					text: field.label,
					value: `customFields.${field.id}`,
					hidden: true,
					width: 150,
					hidable: true,
					fieldType: field.fieldType
				}));
			},
    },
    created() {
      // Initialize columns once in created to avoid multiple initializations
      this.initializeColumns();
    },
		activated() {
			this.initializeColumns();
      // Load tasks if not initial load (initial load is handled by tableOptions watcher)
      if (!this.isInitialLoad) {
        this.loadTasks();
      }
		},
    methods: {
      $formatDate,
      $has,
      async loadTasks() {
        this.loading = true;
        try {
          const options = this.$store.getters['TASK/GET_OPTIONS'];
          const { page, itemsPerPage, sortBy, sortDesc } = this.tableOptions;
          
          const params = {
            ...options,
            typeId: this.$route.params.taskTypeId,
            page: page,
            itemsPerPage: itemsPerPage,
          };
          
          // Add sorting parameters if present
          if (sortBy && sortBy.length > 0) {
            params.sortBy = sortBy[0];
            params.sortDesc = sortDesc[0] || false;
          }
          
          const response = await ganttService.getList(params);
          this.entries = response.data.tasks.items || [];
          this.total = response.data.tasks.total || 0;

          // Phase 2: kick off the batched isolation prefetch as soon as the
          // entries are known. We don't await — TaskIsolateButton.ensureDataFetched
          // calls awaitInFlightPrefetch() internally so each card waits for the
          // batch to resolve before checking the map. This converts what would
          // otherwise be ~N per-card fetches into a single round-trip.
          prefetchTasksIsolation(this.entries, this.$store);
        } catch (error) {
          debugError('Failed to load tasks:', error);
          this.$dialog.notify.error('Failed to load tasks');
        } finally {
          this.loading = false;
        }
      },
      initializeColumns() {
        // Start with default columns (without custom fields)
        // ColumnsVisibilityControl will handle merging with stored settings
        this.availableColumns = [...this.defaultColumns];
        
        // We don't need to load from local storage here as ColumnsVisibilityControl will do that
        // Just ensure custom fields are included in our columns definition
        this.ensureCustomFieldsInColumns();
      },
      ensureCustomFieldsInColumns() {
        // This ensures all custom fields from the current task type are included in availableColumns
        if (!this.customFieldsColumns || !this.customFieldsColumns.length) return;
        
        let lastOrder = Math.max(...this.availableColumns.map(col => col.order || 0), 0);
        
        this.customFieldsColumns.forEach(field => {
          if (!this.availableColumns.find(col => col.value === field.value)) {
            lastOrder++;
            this.availableColumns.push({
              ...field,
              order: lastOrder,
            });
          }
        });
        
        // The v-model binding will ensure ColumnsVisibilityControl gets the updated availableColumns
      },
      update(taskItem = null) {
        this.$refs.editTask.open({
					task: taskItem,
					isNew: false,
					taskTypeId: this.$route.params.taskTypeId,
				});
      },
      del(id) {
        this.$dialog.error({
          text: 'Are You Sure You Want to Delete This Task?',
          title: 'Delete Task',
          actions: {
            false: 'Cancel',
            true: {
              text: 'Ok',
              color: 'primary',
              handle: async () => {
                await this.$store.dispatch('TASK/DELETE', [id]);
                // Reload tasks after delete
                await this.loadTasks();
              },
            },
          },
        });
      },
      getTypeColor(color) {
        return color || 'primary';
      },
      getStatusColor(color) {
        return color || 'grey';
      },
      getCurrentStatusColor(color) {
        return color || 'grey';
      },
      getProgressColor(progress) {
        if (progress >= 100) return 'success';
        if (progress >= 75) return 'info';
        if (progress >= 50) return 'warning';
        if (progress >= 25) return 'orange';
        return 'error';
      },
      getAvatarColor(user) {
        if (!user) return 'primary';
        
        // Generate a consistent color based on user ID or name
        const colors = ['primary', 'success', 'info', 'warning', 'error', 'purple', 'indigo', 'teal', 'orange', 'pink'];
        
        if (user.id) {
          // Use user ID to generate a consistent color
          const hash = user.id.split('-')[0].charCodeAt(0) + 
                      (user.id.split('-')[1] ? user.id.split('-')[1].charCodeAt(0) : 0);
          return colors[hash % colors.length];
        } else if (user.firstName || user.lastName) {
          // Use name to generate a consistent color
          const nameHash = (user.firstName ? user.firstName.charCodeAt(0) : 0) + 
                           (user.lastName ? user.lastName.charCodeAt(0) : 0);
          return colors[nameHash % colors.length];
        }
        
        return 'primary';
      },
      getUserInitials(user) {
        if (!user) return '';
        
        let initials = '';
        
        if (user.firstName) {
          initials += user.firstName.charAt(0).toUpperCase();
        }
        
        if (user.lastName) {
          initials += user.lastName.charAt(0).toUpperCase();
        }
        
        // If no first or last name, try using the first two letters of email
        if (!initials && user.email) {
          initials = user.email.substring(0, 2).toUpperCase();
        }
        
        return initials || '?';
      },
      getCustomFieldValue(item, fieldId) {
        if (!item || !item.customFields || !item.customFields.length) return null;
        
        let value =  item.customFields.find(field => field.fieldId === fieldId)?.value;

				if (!value) return null;

				return value;
      },
      getUserById(user) {
        return this.users.find(u => u.id === user) || this.users.find(u => `${u.firstName} ${u.lastName}` === user)
      },
			getCustomFieldOptionValue(fieldId, value) {
				let customField = this.customFields.find(field => field.id === fieldId);

				return customField.options.find(option => option.id === value);
			},
      /** The tasks this view shows (Isolate All, Task/views/index.vue): this page's rows. */
      tasksOnScreen() {
        return this.entries;
      },
      onTaskUpdated(payload) {
        if (payload.isNew) {
          // For new tasks, add to the beginning of the list
          this.entries.unshift(payload.task);
          this.total += 1;
          this.$dialog.notify.success('Task created successfully');
        } else {
          // For updates, find and replace the existing item
          const index = this.entries.findIndex(entry => entry.id === payload.id);
          if (index !== -1) {
            this.$set(this.entries, index, payload.task);
          }
          this.$dialog.notify.success('Task updated successfully');
        }
      },
      isEditing(itemId, field) {
        return this.editingCell && this.editingCell.itemId === itemId && this.editingCell.field === field;
      },
      isSaving(itemId, field) {
        return this.savingCell && this.savingCell.itemId === itemId && this.savingCell.field === field;
      },
      startEdit(item, field, currentValue) {
        if (!this.$has(this.permissions.Tasks.Update)) return;
        
        // Enhanced logging for date fields
        if (field === 'start_date' || field === 'end_date') {
          debugLog('Date Edit Debug:', {
            field,
            originalValue: item[field],
            convertedValue: currentValue,
            itemData: { id: item.id, [field]: item[field] }
          });
        }
        
        this.editingCell = { itemId: item.id, field };
        this.editingValue = currentValue;
        
        // Focus the editor in the next tick
        this.$nextTick(() => {
          const editorRef = this.$refs[`${field}Editor`];
          if (editorRef && editorRef[0] && editorRef[0].focus) {
            editorRef[0].focus();
          }
        });
      },
      cancelEdit() {
        this.editingCell = null;
        this.editingValue = null;
      },
      async saveEdit(item) {
        if (!this.editingCell || this.editingValue === null) return;
        
        const field = this.editingCell.field;
        const currentValue = this.getFieldValue(item, field);
        
        // Check if value has changed
        if (this.editingValue === currentValue) {
          this.cancelEdit();
          return;
        }
        
        this.savingCell = { itemId: item.id, field };
        
        try {
          // Create a complete task object (like EditTask component does)
          let taskToUpdate = { ...item };
          
          // Update the specific field with the new value
          const apiFieldName = this.getFieldApiName(field);
          taskToUpdate[apiFieldName] = this.editingValue;
          
          // Format dates to YYYY-MM-DD (same as EditTask component)
          if (taskToUpdate.start_date) {
            try {
              taskToUpdate.start_date = dayjs(taskToUpdate.start_date).format('YYYY-MM-DD');
            } catch (e) {
              debugWarn('Failed to format start_date:', taskToUpdate.start_date);
            }
          }
          if (taskToUpdate.end_date) {
            try {
              taskToUpdate.end_date = dayjs(taskToUpdate.end_date).format('YYYY-MM-DD');
            } catch (e) {
              debugWarn('Failed to format end_date:', taskToUpdate.end_date);
            }
          }
          if (taskToUpdate.actualStartDate) {
            try {
              taskToUpdate.actualStartDate = dayjs(taskToUpdate.actualStartDate).format('YYYY-MM-DD');
            } catch (e) {
              debugWarn('Failed to format actualStartDate:', taskToUpdate.actualStartDate);
            }
          }
          if (taskToUpdate.actualEndDate) {
            try {
              taskToUpdate.actualEndDate = dayjs(taskToUpdate.actualEndDate).format('YYYY-MM-DD');
            } catch (e) {
              debugWarn('Failed to format actualEndDate:', taskToUpdate.actualEndDate);
            }
          }
          
          // Ensure we have the required fields for the API
          if (!taskToUpdate.typeId && this.$route.params.taskTypeId) {
            taskToUpdate.typeId = this.$route.params.taskTypeId;
          }
          
          const saved = await this.$store.dispatch('TASK/UPDATE', taskToUpdate).then(() => true, () => false);
          // Refused: TASK/UPDATE already showed the one message. The cell stays in edit with the
          // user's value, and the row keeps what the server has (the store only writes on success).
          if (!saved) return;

          this.$dialog.notify.success(`${this.getFieldLabel(field)} updated successfully`);
          // Reload tasks after update
          await this.loadTasks();
          this.cancelEdit();
        } catch (error) {
          this.$dialog.notify.error(`Failed to update ${this.getFieldLabel(field)}`);
          debugError('Task update error:', error);
        } finally {
          this.savingCell = null;
        }
      },
      getFieldLabel(field) {
        const labels = {
          'status': 'Status',
          'participants': 'Participants', 
          'approvedBy': 'Approved By',
          'reportedBy': 'Reported By',
          'start_date': 'Start Date',
          'end_date': 'End Date'
        };
        return labels[field] || field;
      },
      getFieldValue(item, field) {
        switch (field) {
          case 'status':
            return item.statusId;
          case 'participants':
            return item.participantsIds || [];
          case 'approvedBy':
            return item.approvedById;
          case 'reportedBy':
            return item.reportedById;
          case 'start_date':
            if (!item.start_date) return null;
            try {
              return dayjs(item.start_date).format('YYYY-MM-DD');
            } catch (e) {
              debugWarn('Invalid start_date format:', item.start_date);
              return null;
            }
          case 'end_date':
            if (!item.end_date) return null;
            try {
              return dayjs(item.end_date).format('YYYY-MM-DD');
            } catch (e) {
              debugWarn('Invalid end_date format:', item.end_date);
              return null;
            }
          default:
            return item[field];
        }
      },
      getFieldApiName(field) {
        const apiNames = {
          'status': 'statusId',
          'participants': 'participantsIds',
          'approvedBy': 'approvedById',
          'reportedBy': 'reportedById',
          'start_date': 'start_date',
          'end_date': 'end_date'
        };
        return apiNames[field] || field;
      },
    },
    watch: {
      entries: {
        handler() {
          this.ensureCustomFieldsInColumns();
        },
        deep: false
      },
      taskType: {
        handler() {
          // When task type changes, ensure we update columns to include new custom fields
          this.ensureCustomFieldsInColumns();
        },
        deep: true
      },
      customFieldsColumns: {
        handler() {
          // When custom fields change, ensure they're in our columns
          this.ensureCustomFieldsInColumns();
        },
        deep: true
      },
      '$store.state.TASK.options': {
        handler() {
          // Reset to first page when filters change and reload data
          this.tableOptions.page = 1;
          this.loadTasks();
        },
        deep: true
      },
      tableOptions: {
        handler() {
          // Reload tasks when pagination or sorting changes
          if (this.isInitialLoad) {
            this.isInitialLoad = false;
          }
          this.loadTasks();
        },
        deep: true
      }
    },
  };
</script>

<style lang="scss" scoped>
  .list-view-container {
    height: 100%;
    display: flex;
    flex-direction: column;
		overflow: auto;
  }

  .list-view-header {
    background-color: transparent;
  }

  .list-view-table {
    flex: 1;
    background-color: white;
    margin-right: 16px;
		margin-bottom: 16px;
    border: 1px solid rgba(0, 0, 0, 0.1);
		border-radius: 4px;
    overflow: hidden !important;

		::v-deep .v-data-table__wrapper > table tr td {
			border: 1px solid #e6e6e608;
		}

    ::v-deep .v-data-table__wrapper {
      overflow-x: auto;
			border-radius: 0px !important;
    }

    ::v-deep .v-data-table-header {
      background-color: #f4f5f7;
      th {
        font-weight: 600;
        color: #172b4d;
        white-space: nowrap;
      }
    }

    ::v-deep .v-data-table__mobile-table-row {
      display: initial;
      table-layout: fixed;
      width: 100%;
    }

    ::v-deep .v-data-table__mobile-row {
      height: auto;
      min-height: 48px;
      padding: 8px 16px;
    }
  }

  .description-text {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    color: #6b778c;
  }

  .task-name {
    &:hover {
      color: var(--v-primary-base, #0052cc);
      text-decoration: underline;
      cursor: pointer;
    }
  }

  .type-chip {
    font-weight: 500;
    font-size: 0.875rem;
    justify-content: center;
  }

  .status-chip {
    font-weight: 500;
    text-transform: uppercase;
    font-size: 0.75rem;
    justify-content: center;
  }

  .date-cell {
    display: flex;
    align-items: center;
    color: #6b778c;
    font-size: 0.875rem;
		border: 1px solid #a8a8a875;
    padding: 4px 8px;
    border-radius: 4px;
		width: fit-content;
  }

  .loading-container,
  .no-data-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 48px 0;
    color: #6b778c;
  }

  .column-config-dialog {
    .column-list-card {
      background-color: #f4f5f7;
    }

    .column-list {
      background-color: white;
      border-radius: 3px;
      border: 1px solid #dfe1e6;
    }

    .column-list-item {
      border-bottom: 1px solid #dfe1e6;
      &:last-child {
        border-bottom: none;
      }
    }
  }

  .drag-handle {
    cursor: move;
    color: #6b778c;
  }

  .gap-2 {
    gap: 8px;
  }

  // Custom scrollbar
  ::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  ::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }

  ::-webkit-scrollbar-thumb {
    background: #c1c7d0;
    border-radius: 4px;
    &:hover {
      background: #97a0af;
    }
  }

  .progress-linear {
    min-width: 100px;
  }

  .participants-stack {
    display: flex;
    align-items: center;
		cursor: pointer;
    
    .participant-avatar {
      border: 2px solid white;
      font-size: 12px;
      font-weight: 500;
      transition: transform 0.2s;
      
      &:hover {
        transform: translateY(-3px);
        z-index: 10 !important;
      }
    }
  }

  .user-tooltip {
    padding: 8px;
    border-radius: 4px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    width: 100%;

    .user-tooltip-email {
      font-size: 0.75rem;
    }
  }

  .column-category-header {
    font-weight: 500;
    padding: 8px 16px;
    background-color: #f4f5f7;
    border-radius: 4px;
    margin-bottom: 8px;
    display: flex;
    align-items: center;
    color: #172b4d;
  }

  // Inline editing styles
  .status-chip {
    cursor: pointer;
    transition: opacity 0.2s;
    
    &:hover {
      opacity: 0.8;
    }
  }

  .date-cell {
    cursor: pointer;
    transition: background-color 0.2s;
    
    &:hover {
      background-color: rgba(0, 0, 0, 0.04);
    }
  }

  .participants-stack {
    cursor: pointer;
  }

  .inline-editor {
    min-width: 200px;
  }

  .edit-actions {
    display: flex;
    align-items: center;
    gap: 4px;
    margin-left: 8px;
  }
</style>
